import React, { useState } from 'react';
import axios from 'axios';
import { X, PenLine } from 'lucide-react';
import { StarInput } from './StarRating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewModal({ open, onClose }) {
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim() || rating < 1 || comment.trim().length < 3) {
      setError('Please add your name, a rating and a short comment.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await axios.post(`${API}/reviews`, {
        name: name.trim(),
        rating,
        comment: comment.trim(),
      });
      setDone(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('review submit failed', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setName('');
    setRating(0);
    setComment('');
    setDone(false);
    setError('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      data-testid="review-modal"
    >
      <div className="absolute inset-0 bg-[#2C3A28]/60 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md rounded-3xl bg-[#E9EBED] shadow-2xl overflow-hidden fade-up">
        <div className="h-2 bg-gradient-to-r from-[#2C3A28] via-[#2C3A28] to-[#2C3A28]" />
        <button
          onClick={close}
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-[#2C3A28] hover:bg-[#EDEAE0] transition-colors"
          aria-label="Close"
          data-testid="review-modal-close"
        >
          <X size={20} />
        </button>
        <div className="px-8 pt-9 pb-9">
          {done ? (
            <div className="text-center py-6" data-testid="review-success">
              <h3 className="font-display text-3xl font-semibold text-[#2C3A28]">Grazie mille!</h3>
              <p className="mt-3 text-[#3B2A20]/70 leading-relaxed">
                Your review has been submitted and will appear here once we've approved it.
              </p>
              <button
                onClick={close}
                className="mt-7 rounded-full bg-[#2C3A28] text-white px-7 py-3 font-semibold hover:bg-[#2C3A28] transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-[#EDEAE0] flex items-center justify-center text-[#2C3A28]">
                <PenLine size={26} />
              </div>
              <h3 className="mt-5 font-display text-3xl font-semibold text-[#2C3A28]">
                Leave a review
              </h3>
              <p className="mt-2 text-[#3B2A20]/70">
                Tell us about your experience at Al Naturale.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#2C3A28]">Your rating</label>
                  <div className="mt-2">
                    <StarInput value={rating} onChange={setRating} />
                  </div>
                </div>
                <input
                  data-testid="review-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full rounded-2xl border border-[#2C3A28]/15 bg-white px-5 py-3.5 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                />
                <textarea
                  data-testid="review-comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder="Share your thoughts..."
                  className="w-full rounded-2xl border border-[#2C3A28]/15 bg-white px-5 py-3.5 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors resize-none"
                />
                {error && (
                  <p className="text-sm text-[#2C3A28]" data-testid="review-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="review-submit"
                  className="w-full rounded-full bg-[#2C3A28] text-white py-4 font-semibold hover:bg-[#2C3A28] transition-colors disabled:opacity-60"
                >
                  {busy ? 'Submitting...' : 'Submit review'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
